import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Send, ShieldCheck } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Field, Input, Select } from '@/components/ui/input';
import { toast } from 'sonner';

/**
 * Correo saliente: configuración, prueba de conexión y correo de prueba.
 *
 * La contraseña no viaja de vuelta al navegador: la pantalla solo sabe si hay una guardada y, para
 * cambiarla, se escribe otra. Dejarla en blanco la conserva; el botón «Borrar contraseña» la quita.
 */

interface Config {
  host: string | null; port: number; secure: boolean; username: string | null;
  fromAddress: string | null; fromName: string | null; replyTo: string | null;
  allowInsecure: boolean; enabled: boolean; hasPassword: boolean;
  lastTestAt: string | null; lastTestOk: boolean | null; lastTestError: string | null;
}

const VACIA: Config = {
  host: '', port: 587, secure: false, username: '', fromAddress: '', fromName: '',
  replyTo: '', allowInsecure: false, enabled: false, hasPassword: false, lastTestAt: null, lastTestOk: null, lastTestError: null
};

export function MailPanel() {
  const qc = useQueryClient();
  const [form, setForm] = useState<Config>(VACIA);
  const [password, setPassword] = useState('');
  const [borrarPassword, setBorrarPassword] = useState(false);
  const [destino, setDestino] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);

  const config = useQuery({
    queryKey: ['mail', 'settings'],
    queryFn: () => api<{ data: Config }>('/mail/settings')
  });

  useEffect(() => {
    if (!config.data?.data) return;
    const datos = config.data.data;
    setForm({ ...datos, host: datos.host ?? '', username: datos.username ?? '',
      fromAddress: datos.fromAddress ?? '', fromName: datos.fromName ?? '', replyTo: datos.replyTo ?? '' });
    setPassword('');
    setBorrarPassword(false);
  }, [config.data]);

  const recargar = async () => { await qc.invalidateQueries({ queryKey: ['mail'] }); };

  const guardar = async () => {
    setGuardando(true);
    try {
      await api('/mail/settings', {
        method: 'PUT',
        body: {
          host: form.host || null,
          port: form.port,
          secure: form.secure,
          username: form.username || null,
          // Sin contraseña escrita y sin pedir borrarla, no se toca la que haya.
          ...(borrarPassword ? { password: null } : password ? { password } : {}),
          fromAddress: form.fromAddress || null,
          fromName: form.fromName || null,
          replyTo: form.replyTo || null,
          allowInsecure: form.allowInsecure,
          enabled: form.enabled
        }
      });
      toast.success('Configuración del correo guardada');
      await recargar();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally { setGuardando(false); }
  };

  const probar = async () => {
    setProbando(true);
    try {
      await api('/mail/verify', { method: 'POST', body: {} });
      toast.success('La conexión con el servidor de correo funciona');
      await recargar();
    } catch (error) {
      toast.error(errorMessage(error));
      await recargar();
    } finally { setProbando(false); }
  };

  const enviarPrueba = async () => {
    if (!destino.trim()) { toast.error('Escribe a qué dirección mandar la prueba'); return; }
    setProbando(true);
    try {
      await api('/mail/test', { method: 'POST', body: { to: destino.trim() } });
      toast.success(`Correo de prueba enviado a ${destino.trim()}`);
      await recargar();
    } catch (error) {
      toast.error(errorMessage(error));
      await recargar();
    } finally { setProbando(false); }
  };

  return (
    <div className="space-y-3">
      <Card className="space-y-3 p-3">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Mail className="size-3.5" /> Servidor de correo saliente
          </h3>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            Correo activado
          </label>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Mientras esté desactivado, el envío de pedidos y los avisos por correo responden que no está
          configurado, en vez de fallar en silencio. Para activarlo hacen falta servidor y remitente.
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Servidor (host)" required><Input value={form.host ?? ''} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="smtp.eurobath.es" /></Field>
          <Field label="Puerto" required><Input value={String(form.port)} inputMode="numeric"
                onChange={(e) => setForm({ ...form, port: e.target.value === '' ? 0 : Number(e.target.value) })} /></Field>
          <Field label="Cifrado">
            <Select value={form.secure ? 'ssl' : 'starttls'}
            onChange={(e) => setForm({ ...form, secure: e.target.value === 'ssl' })}>
              <option value="starttls">STARTTLS (587)</option>
              <option value="ssl">SSL directo (465)</option>
            </Select>
          </Field>
          <Field label="Usuario"><Input value={form.username ?? ''} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="erp@eurobath.es" /></Field>
          <Field label="Contraseña" hint={form.hasPassword
              ? 'Hay una guardada. Se conserva salvo que cambies servidor, usuario o el cifrado: ahí se borra'
              : 'Sin contraseña guardada'}>
            <Input type="password" value={password} autoComplete="new-password"
              onChange={(e) => { setPassword(e.target.value); setBorrarPassword(false); }} />
          </Field>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={borrarPassword} onChange={(e) => { setBorrarPassword(e.target.checked); if (e.target.checked) setPassword(''); }} />
              Borrar la contraseña guardada
            </label>
          </div>
          <Field label="Remitente (dirección)" required><Input value={form.fromAddress ?? ''} onChange={(e) => setForm({ ...form, fromAddress: e.target.value })} placeholder="pedidos@eurobath.es" /></Field>
          <Field label="Remitente (nombre)"><Input value={form.fromName ?? ''} onChange={(e) => setForm({ ...form, fromName: e.target.value })} placeholder="Eurobath" /></Field>
          <Field label="Responder a"><Input value={form.replyTo ?? ''} onChange={(e) => setForm({ ...form, replyTo: e.target.value })} placeholder="compras@eurobath.es" /></Field>
        </div>
        <label className="flex items-start gap-2 rounded-md bg-amber-500/10 px-3 py-2 text-[11px]">
          <input type="checkbox" className="mt-0.5" checked={form.allowInsecure}
            onChange={(e) => setForm({ ...form, allowInsecure: e.target.checked })} />
          <span>
            <b>Permitir sin cifrar</b>: solo para un servidor interno que no ofrezca STARTTLS. Si está
            apagado (lo normal), la conexión se rechaza antes de mandar la contraseña.
          </span>
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Button loading={guardando} onClick={() => void guardar()}>Guardar</Button>
          <Button variant="outline" loading={probando} onClick={() => void probar()}>
            <ShieldCheck className="mr-1.5 size-4" /> Probar conexión
          </Button>
          {form.lastTestOk === null ? null : form.lastTestOk ? (
            <Badge tone="success">Última prueba correcta</Badge>
          ) : (
            <Badge tone="danger">La última prueba falló</Badge>
          )}
        </div>
        {form.lastTestError ? (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-[11px] text-destructive">{form.lastTestError}</p>
        ) : null}
      </Card>

      <Card className="space-y-3 p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Enviar un correo de prueba</h3>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Destinatario" className="min-w-64 flex-1">
            <Input value={destino} onChange={(e) => setDestino(e.target.value)} placeholder="tu@correo.es" />
          </Field>
          <Button variant="outline" loading={probando} onClick={() => void enviarPrueba()}>
            <Send className="mr-1.5 size-4" /> Enviar prueba
          </Button>
        </div>
      </Card>
    </div>
  );
}
